// src/lib/i18n/messages.ts
// ─────────────────────────────────────────────────────────────────────────
// Sprint: Frontend UI i18n Foundation V1, Parte A.
//
// A small, flat, typed message table — deliberately NOT a nested-object
// tree (no i18next/react-intl-style `{home: {hero: {headline: "..."}}}`)
// because a flat `Record<MessageKey, string>` keeps `keyof typeof MESSAGES.es`
// a simple, exhaustive string-literal union with zero extra tooling. Keys
// use a `namespace.thing` dot convention purely for human readability while
// scanning this file — TypeScript treats each one as an opaque string.
//
// ES is the SOURCE OF TRUTH: `MessageKey` is derived from `MESSAGES.es`
// alone, and `MESSAGES.en`/`MESSAGES.pt` are each typed as
// `Record<MessageKey, string>` — so adding/renaming/removing a key in ES
// without updating EN/PT is a TypeScript compile error, not a silent
// runtime fallback. (useI18n()'s runtime fallback-to-ES exists only for a
// key that somehow reaches production without having been added here at
// all, or a stale build — never as the expected day-to-day path.)
//
// What this file intentionally does NOT contain (see CLAUDE.md / this
// sprint's own brief): provider_name, product title, product description,
// or any other provider-supplied text — those come from the backend
// as-is, in whatever language the provider itself supports, and are never
// client-side "translated" (that would misrepresent what the provider
// actually offers). Full legal-page bodies (/terminos, /privacidad,
// /cookies, /afiliados, /contacto) are also out of scope for this sprint —
// only their nav/footer LABELS are translated; see legal.onlyInSpanishNote
// below for the one added string those routes use to disclose that.
import type { Language } from "./languages";

const es = {
  // ── Home hero / search ──────────────────────────────────────────────
  "home.hero.eyebrow": "Inteligencia de viaje",
  "home.hero.headline": "Encuentra experiencias para tu viaje",
  "home.hero.subcopy": "Descubre y compara experiencias, actividades y excursiones para tu próximo viaje, sin posiciones patrocinadas.",
  "home.search.ariaLabel": "¿Qué esperas del viaje?",
  "home.search.clearAriaLabel": "Borrar búsqueda",
  "home.search.buttonIdle": "Buscar",
  "home.search.buttonLoading": "Buscando…",
  "home.travelers.solo": "Solo",
  "home.travelers.couple": "Pareja",
  "home.travelers.friends": "Amigos",
  "home.travelers.family": "Familia",
  "home.travelers.seniors": "Adultos mayores",
  "home.travelers.defaultLabel": "Viajeros",
  "home.travelers.popoverHeading": "Tipo de viajero",
  "home.travelers.adults": "Adultos",
  "home.travelers.children": "Niños",
  "home.travelers.reduceAdultsAria": "Reducir adultos",
  "home.travelers.addAdultAria": "Agregar adulto",
  "home.travelers.adultsAgeNote": "18 años o más",
  "home.travelers.childLabel": "Niño {n}",
  "home.travelers.childAgeAria": "Edad del niño {n}",
  "home.travelers.removeChildAria": "Eliminar niño {n}",
  "home.travelers.ageYearsOption": "{n} años",
  "home.travelers.addChild": "Agregar niño",
  "home.search.sectionAriaLabel": "Búsqueda de experiencias",
  "home.featured.surpriseMeAria": "Elegir un destino al azar",
  "home.compactSearch.ariaLabel": "Búsqueda activa",
  "home.geoCoverage.loading": "Consultando cobertura observada",
  "home.geoCoverage.errorTitle": "No pudimos abrir este destino",
  "home.geoCoverage.errorBody": "Selecciona nuevamente un país, continente o región desde el buscador.",
  "home.geoCoverage.exploreByGeo": "Explorar por geografía",
  "home.geoCoverage.summaryFallback": "La cobertura se confirmará ciudad por ciudad.",
  "home.geoCoverage.summaryWithCounts": "{activities} en {cities}.",
  "home.geoCoverage.activitiesObserved": "{count}{suffix} experiencias observadas",
  "home.geoCoverage.inventoryToVerify": "Inventario por verificar",
  "home.geoCoverage.partialDataNote": "Datos para {observed} de {total} ciudades",
  "home.trust.direct.title": "Reserva directa, sin sobreprecio",
  "home.trust.direct.subtitle": "Siempre con el proveedor original.",
  "home.trust.transparent.title": "Siempre sabes con quién reservas",
  "home.trust.transparent.subtitle": "Proveedor visible en cada resultado.",
  "home.trust.neutral.title": "Comparación neutral",
  "home.trust.neutral.subtitle": "Sin posiciones patrocinadas.",

  // ── Sprint: Contact + Brand Trust V1 — "Cómo funciona SiftTrip" ───────
  "home.howItWorks.eyebrow": "Cómo funciona SiftTrip",
  "home.howItWorks.step1": "Buscas un destino o idea de viaje.",
  "home.howItWorks.step2": "Comparamos experiencias disponibles de proveedores.",
  "home.howItWorks.step3": "Ves proveedor, precio de referencia y condiciones.",
  "home.howItWorks.step4": "Reservas directamente con el proveedor original.",
  "home.howItWorks.step5": "SiftTrip puede recibir comisión de afiliado sin costo adicional para el usuario.",
  "home.featured.heading": "Destinos destacados",
  "home.featured.inspireHeading": "Empieza por inspiración",
  "home.featured.surpriseMe": "Sorpréndeme",
  "home.loading": "Cargando…",
  "home.loadingSearch": "Buscando experiencias reales...",

  // ── Marketplace: filters ────────────────────────────────────────────
  "marketplace.filters.provider": "Proveedor",
  "marketplace.filters.providerAriaLabel": "Filtrar por proveedor",
  "marketplace.filters.allProviders": "Todos los proveedores",
  "marketplace.filters.serviceType.experiences": "Experiencias",
  "marketplace.filters.serviceType.transfers": "Traslados y servicios",
  "marketplace.filters.serviceType.all": "Todo",
  "marketplace.filters.serviceType.ariaLabel": "Tipo de inventario",
  "marketplace.filters.price": "Precio",
  "marketplace.filters.priceAriaLabel": "Filtrar por precio",
  "marketplace.filters.maxPrice": "Precio máximo",
  "marketplace.filters.includeTipBased": "Incluir aporte voluntario",
  "marketplace.filters.upToPrefix": "Hasta",
  "marketplace.filters.noLimit": "Sin límite",
  "marketplace.serviceType.transfer": "Traslado",
  "marketplace.serviceType.airportService": "Servicio aeropuerto",
  "marketplace.serviceType.ticket": "Entrada",

  // ── Marketplace: empty / loading / pagination ───────────────────────
  "marketplace.empty.filtered": "No hay experiencias con estos filtros.",
  "marketplace.empty.clearFilters": "Limpiar filtros",
  "marketplace.empty.destination": "No encontramos experiencias reservables para este destino todavía.",
  "marketplace.loading.grid": "Cargando experiencias",
  "marketplace.loading.warmup": "Preparando experiencias",
  "marketplace.loading.retry": "Reintentar",
  "marketplace.loadMore.loading": "Cargando más experiencias…",
  "marketplace.loadMore.preparing": "Preparando más experiencias…",
  "marketplace.loadMore.button": "Ver más experiencias",
  "marketplace.loadMore.noMore": "No hay más experiencias para cargar en esta búsqueda.",
  "marketplace.loadMore.refreshResults": "Actualizar resultados",

  // ── Marketplace: errors (useMarketplaceSearch) ──────────────────────
  "marketplace.errors.loadFailed": "No pudimos cargar experiencias para este destino en este momento.",
  "marketplace.errors.warmupTimeout": "Estamos preparando más experiencias para este destino. Intenta de nuevo en un momento.",
  "marketplace.errors.staleCursor": "Los resultados cambiaron mientras buscabas. Actualiza para ver la lista más reciente.",
  "marketplace.errors.loadMoreFailed": "No pudimos cargar más experiencias en este momento.",

  // ── Marketplace: toolbar / mobile filter drawer ─────────────────────
  "marketplace.toolbar.ariaLabel": "Controles de la búsqueda",
  "marketplace.toolbar.newSearch": "Nueva búsqueda",
  "marketplace.toolbar.filtersOpenAria": "Abrir filtros",
  "marketplace.toolbar.filtersCloseAria": "Cerrar filtros",
  "marketplace.toolbar.filters": "Filtros",
  "marketplace.toolbar.drawerTitle": "Filtrar actividades",
  "marketplace.toolbar.drawerSubtitle": "Refina por proveedor, precio, duración y categoría.",
  "marketplace.toolbar.clearFilters": "Limpiar filtros",
  "marketplace.toolbar.viewResults": "Ver resultados",

  // ── Marketplace: dynamic filter taxonomy chip row ───────────────────
  "marketplace.taxonomy.heading": "Tipos de experiencia",

  // ── Marketplace: launch promo banner ─────────────────────────────────
  "marketplace.promo.title": "Lanzamiento Sift Trip:",
  "marketplace.promo.body": "compara experiencias de múltiples proveedores en un solo lugar.",

  // ── Marketplace: price/availability trust note (card/row/detail) ────
  "marketplace.priceTrust.priceAndAvailability": "Precio y disponibilidad por confirmar",
  "marketplace.priceTrust.priceOrientative": "Precio orientativo",
  "marketplace.priceTrust.availabilityUncertain": "Disponibilidad por confirmar",

  // ── Marketplace: compare / provider CTA / price / badges ───────────
  "marketplace.compare.add": "Añadir a comparar",
  "marketplace.compare.added": "En comparación",
  "marketplace.compare.remove": "Quitar",
  "marketplace.compare.addAria": "Añadir \"{itemTitle}\" a comparar",
  "marketplace.compare.removeAria": "Quitar \"{itemTitle}\" de la comparación",
  "marketplace.compare.limitReachedToast": "Ya tienes {max} actividades en comparación. Quita una para añadir otra.",
  "marketplace.compare.stickyBarAria": "Barra de comparación",
  "marketplace.compare.clearAria": "Vaciar comparación",
  "marketplace.compare.ctaCompare": "Comparar {count} {word}",
  "marketplace.compare.ctaAddAnother": "Añade otra para comparar",
  "marketplace.compare.countOfMax": "Comparar {count} de {max}",
  "marketplace.compare.countOnly": "Comparar {count}",
  "marketplace.compare.atMax": "máximo",
  "marketplace.compare.readyToCompare": "Listo para comparar",
  "marketplace.compare.addAtLeastOneMore": "Añade al menos una experiencia más",
  "marketplace.compare.openCompareAria": "Abrir comparación de {count} actividades",
  "marketplace.compare.addAnotherAria": "Añade otra actividad para poder comparar",
  "marketplace.provider.continueCta": "Continuar en {provider}",
  "marketplace.provider.continueFallback": "Continuar con el proveedor",
  "marketplace.provider.supportingText": "La experiencia seleccionada se abre con {provider}. La reserva se completa ahí.",
  "marketplace.provider.supportingTextFallback": "La experiencia seleccionada se abre con el proveedor. La reserva se completa ahí.",
  "marketplace.provider.noDirectLinkYet": "Este proveedor aún no ofrece un enlace directo para esta experiencia.",
  "marketplace.price.from": "desde {price}",
  "marketplace.price.free": "Gratis",
  "marketplace.price.tipBased": "Aporte voluntario",
  "marketplace.price.disclaimer": "Conversión de referencia. El proveedor confirma el precio final.",
  "marketplace.price.disclaimerUpdated": "Actualizada diariamente ({date}).",
  "marketplace.badge.exactMatch": "Coincidencia exacta",

  // ── Detail drawer ────────────────────────────────────────────────────
  "detail.srOnlyDescription": "Detalle de la experiencia y acciones disponibles.",
  "detail.priceFrom": "Desde {price}",
  "detail.priceConfirmWithProvider": "Precio por confirmar con el proveedor",
  "detail.trustVerified": "Verificado con proveedor",
  "detail.trustLocal": "Referencia local",
  "detail.availableNow": "Reservable ahora",
  "detail.viewDetail": "Ver detalle",
  "detail.viewDetailAria": "Ver detalle de \"{itemTitle}\"",
  "detail.description": "Descripción",
  "detail.richLoading": "Cargando detalles adicionales…",
  "detail.richError": "No pudimos cargar detalles adicionales del proveedor.",
  "detail.cancellation": "Cancelación",
  "detail.cancellationStandard": "Cancelación estándar",
  "detail.cancellationNoRefund": "Sin reembolso",
  "detail.meetingPoint": "Punto de encuentro",
  "detail.endPoint": "Fin:",
  "detail.includes": "Incluye",
  "detail.excludes": "No incluye",
  "detail.languages": "Idiomas",

  // ── Comparison drawer ────────────────────────────────────────────────
  "compare.title": "Comparar {count} experiencias",
  "compare.titleEmpty": "Comparar experiencias",
  "compare.clear": "Vaciar",
  "compare.clearAriaLabel": "Vaciar toda la comparación",
  "compare.lowestPrice": "Menor precio",
  "compare.bestRating": "Mejor calificación",
  "compare.mostReviews": "Más reseñas",
  "compare.shortestDuration": "Duración más corta",
  "compare.matchLabel": "Coincidencia con tu búsqueda",
  "compare.decisionSummaryHeading": "Resumen de decisión",
  "compare.detailsHeading": "Comparar detalles",
  "compare.cancellationFlexible": "Cancelación flexible",
  "compare.empty": "Añade experiencias a comparar para verlas aquí.",
  "compare.sections.priceConfidence": "Precio y confianza",
  "compare.sections.timeFormat": "Tiempo y formato",
  "compare.sections.providerBooking": "Proveedor y reserva",
  "compare.sections.providerDetails": "Detalles del proveedor",
  "compare.rows.priceFrom": "Precio desde",
  "compare.rows.duration": "Duración",
  "compare.rows.provider": "Proveedor",
  "compare.rows.rating": "Rating",
  "compare.rows.reviewCount": "Número de reseñas",
  "compare.rows.serviceType": "Tipo de servicio",
  "compare.rows.mainFocus": "Foco principal",
  "compare.rows.intensity": "Intensidad",
  "compare.rows.recommendedTime": "Momento recomendado",
  "compare.rows.availability": "Disponibilidad",
  "compare.rows.locationNeighborhood": "Ubicación / barrio",
  "compare.rows.cancellation": "Cancelación",
  "compare.rows.languages": "Idiomas",
  "compare.rows.meetingPoint": "Punto de encuentro / recogida",
  "compare.rows.includes": "Incluye",
  "compare.keyDifferences": "Diferencias clave:",
  "compare.linkUnavailable": "Enlace no disponible",
  "compare.localReferenceNoBooking": "Referencia local — sin reserva directa",
  "compare.notInformed": "No informado",
  "compare.availability.notApplicable": "No aplica",
  "compare.availability.pending": "Por confirmar",
  "compare.availability.unavailable": "No disponible",
  "compare.localReferenceRow": "Referencia local",
  "compare.loading": "Cargando…",

  // ── Semantic intent chips (client-derived from safe backend flags) ──
  "semanticIntent.chip.islamicHeritage": "Patrimonio islámico",
  "semanticIntent.chip.history": "Historia",
  "semanticIntent.chip.architecture": "Arquitectura",
  "semanticIntent.chip.families": "Familias",
  "semanticIntent.chip.localFood": "Comida local",
  "semanticIntent.chip.noTransfers": "Sin traslados",
  "semanticIntent.chip.budget": "Económico",
  "semanticIntent.groupAria": "Intención de búsqueda detectada",
  "semanticIntent.removeChipAria": "Quitar filtro: {label}",

  // ── Destination picker ───────────────────────────────────────────────
  "destination.picker.ariaLabel": "¿A dónde vas?",
  "destination.picker.placeholder": "Ciudad, país o continente",
  "destination.picker.searching": "Buscando destinos",
  "destination.entity.city": "Ciudad",
  "destination.entity.country": "País",
  "destination.entity.continent": "Continente",
  "destination.entity.region": "Región",
  "destination.nearbyAirport": "Aeropuerto cercano: {iata}",

  // ── Date controls ─────────────────────────────────────────────────────
  "date.modeTabs.exact": "Fecha exacta",
  "date.modeTabs.flexible": "Mes aproximado",
  "date.modeTabs.none": "Sin fecha",
  "date.trigger.addDates": "Agregar fechas",
  "date.trigger.dateAria": "Fecha: {label}",
  "date.trigger.removeDateAria": "Quitar fecha",
  "date.exact.stepDeparture": "Selecciona tu fecha de salida",
  "date.exact.stepReturn": "Ahora elige la fecha de regreso",
  "date.exact.selectReturn": "Selecciona regreso",
  "date.exact.clearDates": "Limpiar fechas",
  "date.exact.confirmPrefix": "Confirmar — {range}",
  "date.flexible.whenApprox": "¿Cuándo aproximadamente?",
  "date.flexible.duration": "Duración del viaje",
  "date.flexible.flexibility": "Flexibilidad (±días)",
  "date.none.title": "Sin fecha por ahora",
  "date.none.body": "Puedes explorar experiencias sin fecha. La disponibilidad real se confirma cuando agregues fechas.",
  "date.none.continue": "Continuar sin fecha",
  "date.dayAria": "{day} de {month}",

  // ── Nav / footer ─────────────────────────────────────────────────────
  "nav.experiences": "Experiencias",
  "nav.accommodation": "Alojamiento",
  "nav.planner": "Planner",
  "nav.logoHomeAria": "Sift Trip — inicio",
  "nav.primaryAria": "Navegación principal",
  "nav.comingSoon": "Próximamente",
  "nav.comingSoonBadge": "pronto",
  "nav.engineSelectorAria": "Motor: {engine}. Abrir selector",
  "nav.prefsAria": "Preferencias de moneda e idioma",
  "nav.currency": "Moneda",
  "nav.language": "Idioma",
  "nav.signIn": "Iniciar sesión",
  "nav.signInShort": "Entrar",
  "nav.signUp": "Registrarse",
  "nav.signUpShort": "Registro",
  "nav.home": "Inicio",
  "nav.explore": "Explorar",
  "nav.howItWorks": "¿Cómo funciona?",
  "nav.userMenuAria": "Menú de usuario",
  "nav.signOut": "Cerrar sesión",
  "nav.continueWithGoogle": "Continuar con Google",
  "nav.hamburgerMenuAria": "Menú",
  "footer.terms": "Términos",
  "footer.privacy": "Privacidad",
  "footer.cookies": "Cookies",
  "footer.affiliates": "Afiliados",
  "footer.contact": "Contacto",
  // Sprint: Contact + Brand Trust V1
  "footer.positioning": "Comparador de experiencias de viaje. Reservas siempre con el proveedor original.",

  // ── Auth (Sprint: Auth Foundation V1) ────────────────────────────────
  "auth.popover.signInHeading": "Inicia sesión",
  "auth.popover.signUpHeading": "Crea tu cuenta",
  "auth.popover.subcopy": "Guarda tus preferencias y accede desde cualquier dispositivo.",
  "auth.email.label": "Correo electrónico",
  "auth.email.placeholder": "tu@correo.com",
  "auth.email.submit": "Enviar enlace de acceso",
  "auth.email.sending": "Enviando…",
  "auth.email.sent": "Revisa tu correo — te enviamos un enlace para iniciar sesión.",
  "auth.email.orDivider": "o",
  "auth.close": "Cerrar",
  "auth.error.auth_not_configured": "La autenticación no está disponible en este momento.",
  "auth.error.auth_email_required": "Ingresa un correo electrónico.",
  "auth.error.auth_email_failed": "No pudimos enviar el enlace. Intenta de nuevo.",
  // Sprint: Account Value Layer V1 — contextual subcopy shown instead of
  // auth.popover.subcopy when the sign-in popover was opened from a save
  // action (wishlist heart / "Guardar comparación"), never a generic prompt.
  "auth.popover.wishlistSubcopy": "Inicia sesión para guardar experiencias en tu lista de deseos.",
  "auth.popover.comparisonSubcopy": "Inicia sesión para guardar esta comparación.",

  // ── /cuenta (Sprint: Auth Foundation V1) ─────────────────────────────
  "account.title": "Mi cuenta",
  "account.loggedOutHeading": "Inicia sesión para continuar",
  "account.loggedOutBody": "Guarda tu idioma y moneda preferidos, y accede desde cualquier dispositivo.",
  "account.email": "Correo",
  "account.memberSince": "Miembro desde {date}",
  "account.preferencesHeading": "Preferencias",
  "account.notConfigured": "La autenticación no está configurada en esta instancia.",
  "account.backHome": "Volver al inicio",

  // ── /cuenta — Account Value Layer V1: dropdown menu + page sections ──
  "account.menu.profile": "Perfil",
  "account.menu.recentSearches": "Búsquedas recientes",
  "account.menu.wishlist": "Lista de deseos",
  "account.menu.savedComparisons": "Comparaciones guardadas",
  "account.menu.preferences": "Preferencias",
  "account.menu.help": "Ayuda y contacto",

  "account.tabs.overview": "Resumen",
  "account.tabs.profile": "Perfil",
  "account.tabs.sitePreferences": "Preferencias del sitio",
  "account.tabs.travelPreferences": "Preferencias de viaje",
  "account.tabs.recentSearches": "Búsquedas recientes",
  "account.tabs.wishlist": "Lista de deseos",
  "account.tabs.savedComparisons": "Comparaciones guardadas",
  "account.tabs.help": "Ayuda y contacto",

  "account.overview.heading": "Resumen",
  "account.overview.recentSearchesCount": "{count} búsquedas recientes",
  "account.overview.wishlistCount": "{count} experiencias guardadas",
  "account.overview.savedComparisonsCount": "{count} comparaciones guardadas",
  // ── Sprint: Account UX Refinement V1 ──────────────────────────────────
  "account.overview.completionHeading": "Estado del perfil",
  "account.overview.completionCount": "{completed} de {total} completado",
  "account.overview.quickActionsHeading": "Acciones rápidas",
  "account.overview.quickAction.search": "Buscar experiencias",
  "account.overview.quickAction.wishlist": "Ver lista de deseos",
  "account.overview.quickAction.comparisons": "Ver comparaciones",

  "account.sitePreferences.heading": "Preferencias del sitio",

  "account.travelPreferences.heading": "Preferencias de viaje",
  "account.travelPreferences.body": "Así hemos ido aprendiendo tus preferencias a partir de tus búsquedas — no editable todavía.",
  "account.travelPreferences.empty": "Aún no hemos aprendido tus preferencias de viaje. Genera un itinerario para empezar.",
  "account.travelPreferences.generationCount": "{count} itinerarios generados",
  "account.travelPreferences.citiesExplored": "{count} ciudades exploradas",
  "account.travelPreferences.topInterests": "Intereses frecuentes",
  // ── Sprint: Account UX Refinement V1 — editable interest chips ───────
  "account.travelPreferences.interestsHeading": "Elige tus intereses de viaje",
  "account.travelPreferences.interestsBody": "Guarda tus intereses en tu perfil.",
  "account.travelPreferences.save": "Guardar intereses",
  "account.travelPreferences.saving": "Guardando…",
  "account.travelPreferences.saved": "Intereses guardados",
  "account.travelPreferences.saveError": "No pudimos guardar tus intereses. Intenta de nuevo.",
  "account.travelPreferences.learnedHeading": "Aprendido automáticamente",

  "account.recentSearches.heading": "Búsquedas recientes",
  "account.recentSearches.empty": "Busca experiencias para guardar tus búsquedas recientes",
  "account.recentSearches.emptyTitle": "Aún no tienes búsquedas recientes",
  "account.recentSearches.emptyCta": "Buscar experiencias",
  "account.recentSearches.rerun": "Buscar de nuevo",
  "account.recentSearches.remove": "Quitar",
  "account.recentSearches.removeAria": "Quitar búsqueda reciente: {query}",

  "account.wishlist.heading": "Lista de deseos",
  "account.wishlist.empty": "Guarda experiencias con el corazón",
  "account.wishlist.emptyTitle": "Tu lista de deseos está vacía",
  "account.wishlist.emptyCta": "Explorar experiencias",
  "account.wishlist.viewProvider": "Ver oferta",
  "account.wishlist.remove": "Quitar",
  "account.wishlist.removeAria": "Quitar de la lista de deseos: {itemTitle}",

  "account.savedComparisons.heading": "Comparaciones guardadas",
  "account.savedComparisons.empty": "Compara actividades y guarda tu comparación",
  "account.savedComparisons.emptyTitle": "Aún no tienes comparaciones guardadas",
  "account.savedComparisons.emptyCta": "Buscar experiencias",
  "account.savedComparisons.itemCount": "{count} experiencias",
  "account.savedComparisons.remove": "Eliminar",
  "account.savedComparisons.removeAria": "Eliminar comparación guardada",

  "account.help.heading": "Ayuda y contacto",
  "account.help.body": "¿Tienes preguntas o comentarios sobre Sift Trip? Escríbenos.",
  "account.help.cta": "Ir a contacto",

  // ── Profile completion form (Sprint: Account UX Refinement V1) ───────
  "account.profile.formHeading": "Completa tu perfil",
  "account.profile.displayNameLabel": "Nombre",
  "account.profile.displayNamePlaceholder": "Tu nombre",
  "account.profile.phoneLabel": "Teléfono (opcional)",
  "account.profile.phonePlaceholder": "+57 300 000 0000",
  "account.profile.homeCountryLabel": "País de residencia (opcional)",
  "account.profile.homeCountryPlaceholder": "Colombia",
  "account.profile.travelerTypeLabel": "¿Cómo viajas normalmente? (opcional)",
  "account.profile.save": "Guardar cambios",
  "account.profile.saving": "Guardando…",
  "account.profile.saved": "Perfil actualizado",
  "account.profile.saveError": "No pudimos guardar tus cambios. Intenta de nuevo.",

  "account.travelerType.solo": "Solo/a",
  "account.travelerType.pareja": "En pareja",
  "account.travelerType.familia": "En familia",
  "account.travelerType.amigos": "Con amigos",
  "account.travelerType.mayores": "Adultos mayores",

  "account.interest.art_culture": "Arte y cultura",
  "account.interest.history_heritage": "Historia",
  "account.interest.nature_outdoors": "Naturaleza",
  "account.interest.beach_water": "Playa",
  "account.interest.food_drink": "Comida",
  "account.interest.local_life": "Vida local",
  "account.interest.museums_tickets": "Museos",
  "account.interest.adventure": "Aventura",
  "account.interest.family_kids": "Familia",
  "account.interest.romance": "Romántico",
  "account.interest.nightlife": "Vida nocturna",
  "account.interest.free_tours": "Free tours",
  "account.interest.transfers_airport": "Traslados",

  // ── Wishlist heart button (Sprint: Account Value Layer V1) ───────────
  "wishlist.save": "Guardar en lista de deseos",
  "wishlist.remove": "Quitar de lista de deseos",
  "wishlist.error": "No pudimos guardar. Intenta de nuevo.",

  // ── Saved comparisons (Sprint: Account Value Layer V1) ───────────────
  "compare.saveComparison": "Guardar comparación",
  "compare.saveComparison.saved": "Comparación guardada",
  "compare.saveComparison.error": "No pudimos guardar la comparación.",

  // ── /contacto (Sprint: Contact + Brand Trust V1) ──────────────────────
  "contact.title": "Contacto",
  "contact.intro": "Para preguntas sobre SiftTrip, alianzas, afiliados, proveedores, privacidad o soporte general, puedes escribirnos por los siguientes canales oficiales.",
  "contact.channels.heading": "Canales oficiales",
  "contact.email.label": "Email",
  "contact.phone.label": "WhatsApp / Teléfono",
  "contact.whatsapp.cta": "Escribir por WhatsApp",
  "contact.call.cta": "Llamar",
  "contact.social.heading": "Redes sociales",
  "contact.social.body": "También puedes seguir los canales oficiales de SiftTrip para novedades, destinos e información de lanzamiento.",
  "contact.supportExpectation.heading": "Antes de escribir",
  "contact.supportExpectation.body": "Si tu consulta es sobre una reserva, contacta primero al proveedor donde completaste la compra.",
  "contact.provider.heading": "Proveedores y partners",
  "contact.provider.body": "¿Eres proveedor o partner? Escríbenos.",
  "contact.backHome": "Volver a SiftTrip",

  // ── Legal pages (labels only — see this file's own header note) ─────
  "legal.onlyInSpanishNote": "Disponible en español por ahora.",
} as const;

export type MessageKey = keyof typeof es;

const en: Record<MessageKey, string> = {
  "home.hero.eyebrow": "Travel intelligence",
  "home.hero.headline": "Find experiences for your trip",
  "home.hero.subcopy": "Discover and compare experiences, activities, and excursions for your next trip — no sponsored placements.",
  "home.search.ariaLabel": "What are you looking for on your trip?",
  "home.search.clearAriaLabel": "Clear search",
  "home.search.buttonIdle": "Search",
  "home.search.buttonLoading": "Searching…",
  "home.travelers.solo": "Solo",
  "home.travelers.couple": "Couple",
  "home.travelers.friends": "Friends",
  "home.travelers.family": "Family",
  "home.travelers.seniors": "Seniors",
  "home.travelers.defaultLabel": "Travelers",
  "home.travelers.popoverHeading": "Traveler type",
  "home.travelers.adults": "Adults",
  "home.travelers.children": "Children",
  "home.travelers.reduceAdultsAria": "Decrease adults",
  "home.travelers.addAdultAria": "Add adult",
  "home.travelers.adultsAgeNote": "18 years or older",
  "home.travelers.childLabel": "Child {n}",
  "home.travelers.childAgeAria": "Age of child {n}",
  "home.travelers.removeChildAria": "Remove child {n}",
  "home.travelers.ageYearsOption": "{n} years",
  "home.travelers.addChild": "Add child",
  "home.search.sectionAriaLabel": "Experience search",
  "home.featured.surpriseMeAria": "Pick a random destination",
  "home.compactSearch.ariaLabel": "Active search",
  "home.geoCoverage.loading": "Checking observed coverage",
  "home.geoCoverage.errorTitle": "We couldn't open this destination",
  "home.geoCoverage.errorBody": "Select a country, continent, or region again from the search bar.",
  "home.geoCoverage.exploreByGeo": "Explore by geography",
  "home.geoCoverage.summaryFallback": "Coverage will be confirmed city by city.",
  "home.geoCoverage.summaryWithCounts": "{activities} across {cities}.",
  "home.geoCoverage.activitiesObserved": "{count}{suffix} experiences observed",
  "home.geoCoverage.inventoryToVerify": "Inventory to be verified",
  "home.geoCoverage.partialDataNote": "Data for {observed} of {total} cities",
  "home.trust.direct.title": "Direct booking, no markup",
  "home.trust.direct.subtitle": "Always with the original provider.",
  "home.trust.transparent.title": "You always know who you're booking with",
  "home.trust.transparent.subtitle": "Provider visible on every result.",
  "home.trust.neutral.title": "Neutral comparison",
  "home.trust.neutral.subtitle": "No sponsored placements.",

  "home.howItWorks.eyebrow": "How SiftTrip works",
  "home.howItWorks.step1": "You search for a destination or trip idea.",
  "home.howItWorks.step2": "We compare available experiences from providers.",
  "home.howItWorks.step3": "You see the provider, a reference price, and the conditions.",
  "home.howItWorks.step4": "You book directly with the original provider.",
  "home.howItWorks.step5": "SiftTrip may earn an affiliate commission at no extra cost to you.",

  "home.featured.heading": "Featured destinations",
  "home.featured.inspireHeading": "Start with inspiration",
  "home.featured.surpriseMe": "Surprise me",
  "home.loading": "Loading…",
  "home.loadingSearch": "Searching for real experiences...",

  "marketplace.filters.provider": "Provider",
  "marketplace.filters.providerAriaLabel": "Filter by provider",
  "marketplace.filters.allProviders": "All providers",
  "marketplace.filters.serviceType.experiences": "Experiences",
  "marketplace.filters.serviceType.transfers": "Transfers & services",
  "marketplace.filters.serviceType.all": "All",
  "marketplace.filters.serviceType.ariaLabel": "Inventory type",
  "marketplace.filters.price": "Price",
  "marketplace.filters.priceAriaLabel": "Filter by price",
  "marketplace.filters.maxPrice": "Maximum price",
  "marketplace.filters.includeTipBased": "Include pay-what-you-wish",
  "marketplace.filters.upToPrefix": "Up to",
  "marketplace.filters.noLimit": "No limit",
  "marketplace.serviceType.transfer": "Transfer",
  "marketplace.serviceType.airportService": "Airport service",
  "marketplace.serviceType.ticket": "Ticket",

  "marketplace.empty.filtered": "No experiences match these filters.",
  "marketplace.empty.clearFilters": "Clear filters",
  "marketplace.empty.destination": "We haven't found bookable experiences for this destination yet.",
  "marketplace.loading.grid": "Loading experiences",
  "marketplace.loading.warmup": "Preparing experiences",
  "marketplace.loading.retry": "Retry",
  "marketplace.loadMore.loading": "Loading more experiences…",
  "marketplace.loadMore.preparing": "Preparing more experiences…",
  "marketplace.loadMore.button": "See more experiences",
  "marketplace.loadMore.noMore": "There are no more experiences to load for this search.",
  "marketplace.loadMore.refreshResults": "Refresh results",

  "marketplace.errors.loadFailed": "We couldn't load experiences for this destination right now.",
  "marketplace.errors.warmupTimeout": "We're preparing more experiences for this destination. Try again in a moment.",
  "marketplace.errors.staleCursor": "Results changed while you were searching. Refresh to see the latest list.",
  "marketplace.errors.loadMoreFailed": "We couldn't load more experiences right now.",

  "marketplace.toolbar.ariaLabel": "Search controls",
  "marketplace.toolbar.newSearch": "New search",
  "marketplace.toolbar.filtersOpenAria": "Open filters",
  "marketplace.toolbar.filtersCloseAria": "Close filters",
  "marketplace.toolbar.filters": "Filters",
  "marketplace.toolbar.drawerTitle": "Filter activities",
  "marketplace.toolbar.drawerSubtitle": "Refine by provider, price, duration, and category.",
  "marketplace.toolbar.clearFilters": "Clear filters",
  "marketplace.toolbar.viewResults": "View results",

  "marketplace.taxonomy.heading": "Experience types",

  "marketplace.promo.title": "Sift Trip launch:",
  "marketplace.promo.body": "compare experiences from multiple providers in one place.",

  "marketplace.priceTrust.priceAndAvailability": "Price and availability to be confirmed",
  "marketplace.priceTrust.priceOrientative": "Estimated price",
  "marketplace.priceTrust.availabilityUncertain": "Availability to be confirmed",

  "marketplace.compare.add": "Add to compare",
  "marketplace.compare.added": "In comparison",
  "marketplace.compare.remove": "Remove",
  "marketplace.compare.addAria": "Add \"{itemTitle}\" to compare",
  "marketplace.compare.removeAria": "Remove \"{itemTitle}\" from comparison",
  "marketplace.compare.limitReachedToast": "You already have {max} activities in comparison. Remove one to add another.",
  "marketplace.compare.stickyBarAria": "Comparison bar",
  "marketplace.compare.clearAria": "Clear comparison",
  "marketplace.compare.ctaCompare": "Compare {count} {word}",
  "marketplace.compare.ctaAddAnother": "Add another to compare",
  "marketplace.compare.countOfMax": "Compare {count} of {max}",
  "marketplace.compare.countOnly": "Compare {count}",
  "marketplace.compare.atMax": "max",
  "marketplace.compare.readyToCompare": "Ready to compare",
  "marketplace.compare.addAtLeastOneMore": "Add at least one more experience",
  "marketplace.compare.openCompareAria": "Open comparison of {count} activities",
  "marketplace.compare.addAnotherAria": "Add another activity to compare",
  "marketplace.provider.continueCta": "Continue on {provider}",
  "marketplace.provider.continueFallback": "Continue with the provider",
  "marketplace.provider.supportingText": "The selected experience opens with {provider}. Booking is completed there.",
  "marketplace.provider.supportingTextFallback": "The selected experience opens with the provider. Booking is completed there.",
  "marketplace.provider.noDirectLinkYet": "This provider doesn't offer a direct link for this experience yet.",
  "marketplace.price.from": "from {price}",
  "marketplace.price.free": "Free",
  "marketplace.price.tipBased": "Pay what you wish",
  "marketplace.price.disclaimer": "Reference conversion. The provider confirms the final price.",
  "marketplace.price.disclaimerUpdated": "Updated daily ({date}).",
  "marketplace.badge.exactMatch": "Exact match",

  "detail.srOnlyDescription": "Experience details and available actions.",
  "detail.priceFrom": "From {price}",
  "detail.priceConfirmWithProvider": "Price to be confirmed with the provider",
  "detail.trustVerified": "Verified with provider",
  "detail.trustLocal": "Local reference",
  "detail.availableNow": "Bookable now",
  "detail.viewDetail": "View details",
  "detail.viewDetailAria": "View details for \"{itemTitle}\"",
  "detail.description": "Description",
  "detail.richLoading": "Loading additional details…",
  "detail.richError": "We couldn't load additional details from the provider.",
  "detail.cancellation": "Cancellation",
  "detail.cancellationStandard": "Standard cancellation",
  "detail.cancellationNoRefund": "Non-refundable",
  "detail.meetingPoint": "Meeting point",
  "detail.endPoint": "End:",
  "detail.includes": "Includes",
  "detail.excludes": "Excludes",
  "detail.languages": "Languages",

  "compare.title": "Compare {count} experiences",
  "compare.titleEmpty": "Compare experiences",
  "compare.clear": "Clear",
  "compare.clearAriaLabel": "Clear the entire comparison",
  "compare.lowestPrice": "Lowest price",
  "compare.bestRating": "Best rating",
  "compare.mostReviews": "Most reviews",
  "compare.shortestDuration": "Shortest duration",
  "compare.matchLabel": "Match with your search",
  "compare.decisionSummaryHeading": "Decision summary",
  "compare.detailsHeading": "Compare details",
  "compare.cancellationFlexible": "Flexible cancellation",
  "compare.empty": "Add experiences to compare to see them here.",
  "compare.sections.priceConfidence": "Price & trust",
  "compare.sections.timeFormat": "Time & format",
  "compare.sections.providerBooking": "Provider & booking",
  "compare.sections.providerDetails": "Provider details",
  "compare.rows.priceFrom": "Price from",
  "compare.rows.duration": "Duration",
  "compare.rows.provider": "Provider",
  "compare.rows.rating": "Rating",
  "compare.rows.reviewCount": "Number of reviews",
  "compare.rows.serviceType": "Service type",
  "compare.rows.mainFocus": "Main focus",
  "compare.rows.intensity": "Intensity",
  "compare.rows.recommendedTime": "Recommended time",
  "compare.rows.availability": "Availability",
  "compare.rows.locationNeighborhood": "Location / neighborhood",
  "compare.rows.cancellation": "Cancellation",
  "compare.rows.languages": "Languages",
  "compare.rows.meetingPoint": "Meeting point / pickup",
  "compare.rows.includes": "Includes",
  "compare.keyDifferences": "Key differences:",
  "compare.linkUnavailable": "Link unavailable",
  "compare.localReferenceNoBooking": "Local reference — no direct booking",
  "compare.notInformed": "Not informed",
  "compare.availability.notApplicable": "Not applicable",
  "compare.availability.pending": "To be confirmed",
  "compare.availability.unavailable": "Unavailable",
  "compare.localReferenceRow": "Local reference",
  "compare.loading": "Loading…",

  "semanticIntent.chip.islamicHeritage": "Islamic heritage",
  "semanticIntent.chip.history": "History",
  "semanticIntent.chip.architecture": "Architecture",
  "semanticIntent.chip.families": "Families",
  "semanticIntent.chip.localFood": "Local food",
  "semanticIntent.chip.noTransfers": "No transfers",
  "semanticIntent.chip.budget": "Budget-friendly",
  "semanticIntent.groupAria": "Detected search intent",
  "semanticIntent.removeChipAria": "Remove filter: {label}",

  "destination.picker.ariaLabel": "Where are you going?",
  "destination.picker.placeholder": "City, country, or continent",
  "destination.picker.searching": "Searching destinations",
  "destination.entity.city": "City",
  "destination.entity.country": "Country",
  "destination.entity.continent": "Continent",
  "destination.entity.region": "Region",
  "destination.nearbyAirport": "Nearby airport: {iata}",

  "date.modeTabs.exact": "Exact date",
  "date.modeTabs.flexible": "Approximate month",
  "date.modeTabs.none": "No date",
  "date.trigger.addDates": "Add dates",
  "date.trigger.dateAria": "Date: {label}",
  "date.trigger.removeDateAria": "Remove date",
  "date.exact.stepDeparture": "Select your departure date",
  "date.exact.stepReturn": "Now choose your return date",
  "date.exact.selectReturn": "Select return",
  "date.exact.clearDates": "Clear dates",
  "date.exact.confirmPrefix": "Confirm — {range}",
  "date.flexible.whenApprox": "Roughly when?",
  "date.flexible.duration": "Trip duration",
  "date.flexible.flexibility": "Flexibility (±days)",
  "date.none.title": "No date for now",
  "date.none.body": "You can explore experiences without a date. Real availability is confirmed once you add dates.",
  "date.none.continue": "Continue without a date",
  "date.dayAria": "{month} {day}",

  "nav.experiences": "Experiences",
  "nav.accommodation": "Accommodation",
  "nav.planner": "Planner",
  "nav.logoHomeAria": "Sift Trip — home",
  "nav.primaryAria": "Main navigation",
  "nav.comingSoon": "Coming soon",
  "nav.comingSoonBadge": "soon",
  "nav.engineSelectorAria": "Engine: {engine}. Open selector",
  "nav.prefsAria": "Currency and language preferences",
  "nav.currency": "Currency",
  "nav.language": "Language",
  "nav.signIn": "Sign in",
  "nav.signInShort": "Sign in",
  "nav.signUp": "Create account",
  "nav.signUpShort": "Sign up",
  "nav.home": "Home",
  "nav.explore": "Explore",
  "nav.howItWorks": "How it works?",
  "nav.userMenuAria": "User menu",
  "nav.signOut": "Sign out",
  "nav.continueWithGoogle": "Continue with Google",
  "nav.hamburgerMenuAria": "Menu",
  "footer.terms": "Terms",
  "footer.privacy": "Privacy",
  "footer.cookies": "Cookies",
  "footer.affiliates": "Affiliates",
  "footer.contact": "Contact",
  "footer.positioning": "A travel experience comparison tool. You always book with the original provider.",

  "auth.popover.signInHeading": "Sign in",
  "auth.popover.signUpHeading": "Create your account",
  "auth.popover.subcopy": "Save your preferences and access them from any device.",
  "auth.email.label": "Email address",
  "auth.email.placeholder": "you@email.com",
  "auth.email.submit": "Send sign-in link",
  "auth.email.sending": "Sending…",
  "auth.email.sent": "Check your email — we sent you a sign-in link.",
  "auth.email.orDivider": "or",
  "auth.close": "Close",
  "auth.error.auth_not_configured": "Authentication isn't available right now.",
  "auth.error.auth_email_required": "Enter an email address.",
  "auth.error.auth_email_failed": "We couldn't send the link. Please try again.",
  "auth.popover.wishlistSubcopy": "Sign in to save experiences to your wishlist.",
  "auth.popover.comparisonSubcopy": "Sign in to save this comparison.",

  "account.title": "My account",
  "account.loggedOutHeading": "Sign in to continue",
  "account.loggedOutBody": "Save your preferred language and currency, and access them from any device.",
  "account.email": "Email",
  "account.memberSince": "Member since {date}",
  "account.preferencesHeading": "Preferences",
  "account.notConfigured": "Authentication isn't configured for this instance.",
  "account.backHome": "Back to home",

  "account.menu.profile": "Profile",
  "account.menu.recentSearches": "Recent searches",
  "account.menu.wishlist": "Wishlist",
  "account.menu.savedComparisons": "Saved comparisons",
  "account.menu.preferences": "Preferences",
  "account.menu.help": "Help & contact",

  "account.tabs.overview": "Overview",
  "account.tabs.profile": "Profile",
  "account.tabs.sitePreferences": "Site preferences",
  "account.tabs.travelPreferences": "Travel preferences",
  "account.tabs.recentSearches": "Recent searches",
  "account.tabs.wishlist": "Wishlist",
  "account.tabs.savedComparisons": "Saved comparisons",
  "account.tabs.help": "Help & contact",

  "account.overview.heading": "Overview",
  "account.overview.recentSearchesCount": "{count} recent searches",
  "account.overview.wishlistCount": "{count} saved experiences",
  "account.overview.savedComparisonsCount": "{count} saved comparisons",
  "account.overview.completionHeading": "Profile status",
  "account.overview.completionCount": "{completed} of {total} completed",
  "account.overview.quickActionsHeading": "Quick actions",
  "account.overview.quickAction.search": "Search experiences",
  "account.overview.quickAction.wishlist": "View wishlist",
  "account.overview.quickAction.comparisons": "View comparisons",

  "account.sitePreferences.heading": "Site preferences",

  "account.travelPreferences.heading": "Travel preferences",
  "account.travelPreferences.body": "This is what we've learned from your searches so far — not editable yet.",
  "account.travelPreferences.empty": "We haven't learned your travel preferences yet. Generate an itinerary to get started.",
  "account.travelPreferences.generationCount": "{count} itineraries generated",
  "account.travelPreferences.citiesExplored": "{count} cities explored",
  "account.travelPreferences.topInterests": "Frequent interests",
  "account.travelPreferences.interestsHeading": "Choose your travel interests",
  "account.travelPreferences.interestsBody": "Save your interests to your profile.",
  "account.travelPreferences.save": "Save interests",
  "account.travelPreferences.saving": "Saving…",
  "account.travelPreferences.saved": "Interests saved",
  "account.travelPreferences.saveError": "We couldn't save your interests. Please try again.",
  "account.travelPreferences.learnedHeading": "Learned automatically",

  "account.recentSearches.heading": "Recent searches",
  "account.recentSearches.empty": "Search for experiences to save your recent searches",
  "account.recentSearches.emptyTitle": "No recent searches yet",
  "account.recentSearches.emptyCta": "Search experiences",
  "account.recentSearches.rerun": "Search again",
  "account.recentSearches.remove": "Remove",
  "account.recentSearches.removeAria": "Remove recent search: {query}",

  "account.wishlist.heading": "Wishlist",
  "account.wishlist.empty": "Save experiences with the heart icon",
  "account.wishlist.emptyTitle": "Your wishlist is empty",
  "account.wishlist.emptyCta": "Explore experiences",
  "account.wishlist.viewProvider": "View offer",
  "account.wishlist.remove": "Remove",
  "account.wishlist.removeAria": "Remove from wishlist: {itemTitle}",

  "account.savedComparisons.heading": "Saved comparisons",
  "account.savedComparisons.empty": "Compare activities and save your comparison",
  "account.savedComparisons.emptyTitle": "No saved comparisons yet",
  "account.savedComparisons.emptyCta": "Search experiences",
  "account.savedComparisons.itemCount": "{count} experiences",
  "account.savedComparisons.remove": "Delete",
  "account.savedComparisons.removeAria": "Delete saved comparison",

  "account.help.heading": "Help & contact",
  "account.help.body": "Questions or feedback about Sift Trip? Get in touch.",
  "account.help.cta": "Go to contact",

  "account.profile.formHeading": "Complete your profile",
  "account.profile.displayNameLabel": "Name",
  "account.profile.displayNamePlaceholder": "Your name",
  "account.profile.phoneLabel": "Phone (optional)",
  "account.profile.phonePlaceholder": "+1 555 000 0000",
  "account.profile.homeCountryLabel": "Home country (optional)",
  "account.profile.homeCountryPlaceholder": "United States",
  "account.profile.travelerTypeLabel": "How do you usually travel? (optional)",
  "account.profile.save": "Save changes",
  "account.profile.saving": "Saving…",
  "account.profile.saved": "Profile updated",
  "account.profile.saveError": "We couldn't save your changes. Please try again.",

  "account.travelerType.solo": "Solo",
  "account.travelerType.pareja": "As a couple",
  "account.travelerType.familia": "With family",
  "account.travelerType.amigos": "With friends",
  "account.travelerType.mayores": "Older travelers",

  "account.interest.art_culture": "Art & culture",
  "account.interest.history_heritage": "History",
  "account.interest.nature_outdoors": "Nature",
  "account.interest.beach_water": "Beach",
  "account.interest.food_drink": "Food",
  "account.interest.local_life": "Local life",
  "account.interest.museums_tickets": "Museums",
  "account.interest.adventure": "Adventure",
  "account.interest.family_kids": "Family",
  "account.interest.romance": "Romantic",
  "account.interest.nightlife": "Nightlife",
  "account.interest.free_tours": "Free tours",
  "account.interest.transfers_airport": "Transfers",

  "wishlist.save": "Save to wishlist",
  "wishlist.remove": "Remove from wishlist",
  "wishlist.error": "We couldn't save it. Please try again.",

  "compare.saveComparison": "Save comparison",
  "compare.saveComparison.saved": "Comparison saved",
  "compare.saveComparison.error": "We couldn't save the comparison.",

  "contact.title": "Contact",
  "contact.intro": "For questions about SiftTrip, partnerships, affiliates, providers, privacy, or general support, you can reach us through the following official channels.",
  "contact.channels.heading": "Official channels",
  "contact.email.label": "Email",
  "contact.phone.label": "WhatsApp / Phone",
  "contact.whatsapp.cta": "Message on WhatsApp",
  "contact.call.cta": "Call",
  "contact.social.heading": "Social media",
  "contact.social.body": "You can also follow SiftTrip's official channels for news, destinations, and launch updates.",
  "contact.supportExpectation.heading": "Before you write to us",
  "contact.supportExpectation.body": "If your question is about a booking, please contact the provider where you completed the purchase first.",
  "contact.provider.heading": "Providers & partners",
  "contact.provider.body": "Are you a provider or partner? Get in touch.",
  "contact.backHome": "Back to SiftTrip",

  "legal.onlyInSpanishNote": "Available in Spanish for now.",
};

const pt: Record<MessageKey, string> = {
  "home.hero.eyebrow": "Inteligência de viagem",
  "home.hero.headline": "Encontre experiências para sua viagem",
  "home.hero.subcopy": "Descubra e compare experiências, atividades e passeios para sua próxima viagem, sem posições patrocinadas.",
  "home.search.ariaLabel": "O que você espera da viagem?",
  "home.search.clearAriaLabel": "Limpar busca",
  "home.search.buttonIdle": "Buscar",
  "home.search.buttonLoading": "Buscando…",
  "home.travelers.solo": "Sozinho",
  "home.travelers.couple": "Casal",
  "home.travelers.friends": "Amigos",
  "home.travelers.family": "Família",
  "home.travelers.seniors": "Idosos",
  "home.travelers.defaultLabel": "Viajantes",
  "home.travelers.popoverHeading": "Tipo de viajante",
  "home.travelers.adults": "Adultos",
  "home.travelers.children": "Crianças",
  "home.travelers.reduceAdultsAria": "Diminuir adultos",
  "home.travelers.addAdultAria": "Adicionar adulto",
  "home.travelers.adultsAgeNote": "18 anos ou mais",
  "home.travelers.childLabel": "Criança {n}",
  "home.travelers.childAgeAria": "Idade da criança {n}",
  "home.travelers.removeChildAria": "Remover criança {n}",
  "home.travelers.ageYearsOption": "{n} anos",
  "home.travelers.addChild": "Adicionar criança",
  "home.search.sectionAriaLabel": "Busca de experiências",
  "home.featured.surpriseMeAria": "Escolher um destino aleatório",
  "home.compactSearch.ariaLabel": "Busca ativa",
  "home.geoCoverage.loading": "Consultando cobertura observada",
  "home.geoCoverage.errorTitle": "Não conseguimos abrir este destino",
  "home.geoCoverage.errorBody": "Selecione novamente um país, continente ou região no buscador.",
  "home.geoCoverage.exploreByGeo": "Explorar por geografia",
  "home.geoCoverage.summaryFallback": "A cobertura será confirmada cidade por cidade.",
  "home.geoCoverage.summaryWithCounts": "{activities} em {cities}.",
  "home.geoCoverage.activitiesObserved": "{count}{suffix} experiências observadas",
  "home.geoCoverage.inventoryToVerify": "Inventário a verificar",
  "home.geoCoverage.partialDataNote": "Dados para {observed} de {total} cidades",
  "home.trust.direct.title": "Reserva direta, sem sobretaxa",
  "home.trust.direct.subtitle": "Sempre com o fornecedor original.",
  "home.trust.transparent.title": "Você sempre sabe com quem está reservando",
  "home.trust.transparent.subtitle": "Fornecedor visível em cada resultado.",
  "home.trust.neutral.title": "Comparação neutra",
  "home.trust.neutral.subtitle": "Sem posições patrocinadas.",

  "home.howItWorks.eyebrow": "Como funciona o SiftTrip",
  "home.howItWorks.step1": "Você busca um destino ou ideia de viagem.",
  "home.howItWorks.step2": "Comparamos experiências disponíveis de fornecedores.",
  "home.howItWorks.step3": "Você vê o fornecedor, um preço de referência e as condições.",
  "home.howItWorks.step4": "Você reserva diretamente com o fornecedor original.",
  "home.howItWorks.step5": "O SiftTrip pode receber uma comissão de afiliado sem custo adicional para você.",

  "home.featured.heading": "Destinos em destaque",
  "home.featured.inspireHeading": "Comece com inspiração",
  "home.featured.surpriseMe": "Me surpreenda",
  "home.loading": "Carregando…",
  "home.loadingSearch": "Buscando experiências reais...",

  "marketplace.filters.provider": "Fornecedor",
  "marketplace.filters.providerAriaLabel": "Filtrar por fornecedor",
  "marketplace.filters.allProviders": "Todos os fornecedores",
  "marketplace.filters.serviceType.experiences": "Experiências",
  "marketplace.filters.serviceType.transfers": "Traslados e serviços",
  "marketplace.filters.serviceType.all": "Tudo",
  "marketplace.filters.serviceType.ariaLabel": "Tipo de inventário",
  "marketplace.filters.price": "Preço",
  "marketplace.filters.priceAriaLabel": "Filtrar por preço",
  "marketplace.filters.maxPrice": "Preço máximo",
  "marketplace.filters.includeTipBased": "Incluir contribuição voluntária",
  "marketplace.filters.upToPrefix": "Até",
  "marketplace.filters.noLimit": "Sem limite",
  "marketplace.serviceType.transfer": "Traslado",
  "marketplace.serviceType.airportService": "Serviço de aeroporto",
  "marketplace.serviceType.ticket": "Ingresso",

  "marketplace.empty.filtered": "Nenhuma experiência corresponde a esses filtros.",
  "marketplace.empty.clearFilters": "Limpar filtros",
  "marketplace.empty.destination": "Ainda não encontramos experiências reserváveis para este destino.",
  "marketplace.loading.grid": "Carregando experiências",
  "marketplace.loading.warmup": "Preparando experiências",
  "marketplace.loading.retry": "Tentar novamente",
  "marketplace.loadMore.loading": "Carregando mais experiências…",
  "marketplace.loadMore.preparing": "Preparando mais experiências…",
  "marketplace.loadMore.button": "Ver mais experiências",
  "marketplace.loadMore.noMore": "Não há mais experiências para carregar nesta busca.",
  "marketplace.loadMore.refreshResults": "Atualizar resultados",

  "marketplace.errors.loadFailed": "Não conseguimos carregar experiências para este destino no momento.",
  "marketplace.errors.warmupTimeout": "Estamos preparando mais experiências para este destino. Tente novamente em instantes.",
  "marketplace.errors.staleCursor": "Os resultados mudaram enquanto você buscava. Atualize para ver a lista mais recente.",
  "marketplace.errors.loadMoreFailed": "Não conseguimos carregar mais experiências no momento.",

  "marketplace.toolbar.ariaLabel": "Controles da busca",
  "marketplace.toolbar.newSearch": "Nova busca",
  "marketplace.toolbar.filtersOpenAria": "Abrir filtros",
  "marketplace.toolbar.filtersCloseAria": "Fechar filtros",
  "marketplace.toolbar.filters": "Filtros",
  "marketplace.toolbar.drawerTitle": "Filtrar atividades",
  "marketplace.toolbar.drawerSubtitle": "Refine por fornecedor, preço, duração e categoria.",
  "marketplace.toolbar.clearFilters": "Limpar filtros",
  "marketplace.toolbar.viewResults": "Ver resultados",

  "marketplace.taxonomy.heading": "Tipos de experiência",

  "marketplace.promo.title": "Lançamento Sift Trip:",
  "marketplace.promo.body": "compare experiências de vários fornecedores em um só lugar.",

  "marketplace.priceTrust.priceAndAvailability": "Preço e disponibilidade a confirmar",
  "marketplace.priceTrust.priceOrientative": "Preço estimado",
  "marketplace.priceTrust.availabilityUncertain": "Disponibilidade a confirmar",

  "marketplace.compare.add": "Adicionar à comparação",
  "marketplace.compare.added": "Em comparação",
  "marketplace.compare.remove": "Remover",
  "marketplace.compare.addAria": "Adicionar \"{itemTitle}\" à comparação",
  "marketplace.compare.removeAria": "Remover \"{itemTitle}\" da comparação",
  "marketplace.compare.limitReachedToast": "Você já tem {max} atividades em comparação. Remova uma para adicionar outra.",
  "marketplace.compare.stickyBarAria": "Barra de comparação",
  "marketplace.compare.clearAria": "Limpar comparação",
  "marketplace.compare.ctaCompare": "Comparar {count} {word}",
  "marketplace.compare.ctaAddAnother": "Adicione outra para comparar",
  "marketplace.compare.countOfMax": "Comparar {count} de {max}",
  "marketplace.compare.countOnly": "Comparar {count}",
  "marketplace.compare.atMax": "máximo",
  "marketplace.compare.readyToCompare": "Pronto para comparar",
  "marketplace.compare.addAtLeastOneMore": "Adicione pelo menos mais uma experiência",
  "marketplace.compare.openCompareAria": "Abrir comparação de {count} atividades",
  "marketplace.compare.addAnotherAria": "Adicione outra atividade para poder comparar",
  "marketplace.provider.continueCta": "Continuar em {provider}",
  "marketplace.provider.continueFallback": "Continuar com o fornecedor",
  "marketplace.provider.supportingText": "A experiência selecionada abre com {provider}. A reserva é concluída lá.",
  "marketplace.provider.supportingTextFallback": "A experiência selecionada abre com o fornecedor. A reserva é concluída lá.",
  "marketplace.provider.noDirectLinkYet": "Este fornecedor ainda não oferece um link direto para esta experiência.",
  "marketplace.price.from": "a partir de {price}",
  "marketplace.price.free": "Grátis",
  "marketplace.price.tipBased": "Contribuição voluntária",
  "marketplace.price.disclaimer": "Conversão de referência. O fornecedor confirma o preço final.",
  "marketplace.price.disclaimerUpdated": "Atualizada diariamente ({date}).",
  "marketplace.badge.exactMatch": "Correspondência exata",

  "detail.srOnlyDescription": "Detalhes da experiência e ações disponíveis.",
  "detail.priceFrom": "A partir de {price}",
  "detail.priceConfirmWithProvider": "Preço a confirmar com o fornecedor",
  "detail.trustVerified": "Verificado com fornecedor",
  "detail.trustLocal": "Referência local",
  "detail.availableNow": "Reservável agora",
  "detail.viewDetail": "Ver detalhes",
  "detail.viewDetailAria": "Ver detalhes de \"{itemTitle}\"",
  "detail.description": "Descrição",
  "detail.richLoading": "Carregando detalhes adicionais…",
  "detail.richError": "Não foi possível carregar detalhes adicionais do fornecedor.",
  "detail.cancellation": "Cancelamento",
  "detail.cancellationStandard": "Cancelamento padrão",
  "detail.cancellationNoRefund": "Sem reembolso",
  "detail.meetingPoint": "Ponto de encontro",
  "detail.endPoint": "Fim:",
  "detail.includes": "Inclui",
  "detail.excludes": "Não inclui",
  "detail.languages": "Idiomas",

  "compare.title": "Comparar {count} experiências",
  "compare.titleEmpty": "Comparar experiências",
  "compare.clear": "Limpar",
  "compare.clearAriaLabel": "Limpar toda a comparação",
  "compare.lowestPrice": "Menor preço",
  "compare.bestRating": "Melhor avaliação",
  "compare.mostReviews": "Mais avaliações",
  "compare.shortestDuration": "Duração mais curta",
  "compare.matchLabel": "Correspondência com sua busca",
  "compare.decisionSummaryHeading": "Resumo de decisão",
  "compare.detailsHeading": "Comparar detalhes",
  "compare.cancellationFlexible": "Cancelamento flexível",
  "compare.empty": "Adicione experiências para comparar e vê-las aqui.",
  "compare.sections.priceConfidence": "Preço e confiança",
  "compare.sections.timeFormat": "Tempo e formato",
  "compare.sections.providerBooking": "Fornecedor e reserva",
  "compare.sections.providerDetails": "Detalhes do fornecedor",
  "compare.rows.priceFrom": "Preço a partir de",
  "compare.rows.duration": "Duração",
  "compare.rows.provider": "Fornecedor",
  "compare.rows.rating": "Avaliação",
  "compare.rows.reviewCount": "Número de avaliações",
  "compare.rows.serviceType": "Tipo de serviço",
  "compare.rows.mainFocus": "Foco principal",
  "compare.rows.intensity": "Intensidade",
  "compare.rows.recommendedTime": "Momento recomendado",
  "compare.rows.availability": "Disponibilidade",
  "compare.rows.locationNeighborhood": "Localização / bairro",
  "compare.rows.cancellation": "Cancelamento",
  "compare.rows.languages": "Idiomas",
  "compare.rows.meetingPoint": "Ponto de encontro / retirada",
  "compare.rows.includes": "Inclui",
  "compare.keyDifferences": "Diferenças-chave:",
  "compare.linkUnavailable": "Link indisponível",
  "compare.localReferenceNoBooking": "Referência local — sem reserva direta",
  "compare.notInformed": "Não informado",
  "compare.availability.notApplicable": "Não aplicável",
  "compare.availability.pending": "A confirmar",
  "compare.availability.unavailable": "Indisponível",
  "compare.localReferenceRow": "Referência local",
  "compare.loading": "Carregando…",

  "semanticIntent.chip.islamicHeritage": "Patrimônio islâmico",
  "semanticIntent.chip.history": "História",
  "semanticIntent.chip.architecture": "Arquitetura",
  "semanticIntent.chip.families": "Famílias",
  "semanticIntent.chip.localFood": "Comida local",
  "semanticIntent.chip.noTransfers": "Sem traslados",
  "semanticIntent.chip.budget": "Econômico",
  "semanticIntent.groupAria": "Intenção de busca detectada",
  "semanticIntent.removeChipAria": "Remover filtro: {label}",

  "destination.picker.ariaLabel": "Para onde você vai?",
  "destination.picker.placeholder": "Cidade, país ou continente",
  "destination.picker.searching": "Buscando destinos",
  "destination.entity.city": "Cidade",
  "destination.entity.country": "País",
  "destination.entity.continent": "Continente",
  "destination.entity.region": "Região",
  "destination.nearbyAirport": "Aeroporto próximo: {iata}",

  "date.modeTabs.exact": "Data exata",
  "date.modeTabs.flexible": "Mês aproximado",
  "date.modeTabs.none": "Sem data",
  "date.trigger.addDates": "Adicionar datas",
  "date.trigger.dateAria": "Data: {label}",
  "date.trigger.removeDateAria": "Remover data",
  "date.exact.stepDeparture": "Selecione sua data de partida",
  "date.exact.stepReturn": "Agora escolha sua data de volta",
  "date.exact.selectReturn": "Selecione a volta",
  "date.exact.clearDates": "Limpar datas",
  "date.exact.confirmPrefix": "Confirmar — {range}",
  "date.flexible.whenApprox": "Quando, aproximadamente?",
  "date.flexible.duration": "Duração da viagem",
  "date.flexible.flexibility": "Flexibilidade (±dias)",
  "date.none.title": "Sem data por enquanto",
  "date.none.body": "Você pode explorar experiências sem data. A disponibilidade real é confirmada quando você adicionar datas.",
  "date.none.continue": "Continuar sem data",
  "date.dayAria": "{day} de {month}",

  "nav.experiences": "Experiências",
  "nav.accommodation": "Hospedagem",
  "nav.planner": "Planner",
  "nav.logoHomeAria": "Sift Trip — início",
  "nav.primaryAria": "Navegação principal",
  "nav.comingSoon": "Em breve",
  "nav.comingSoonBadge": "em breve",
  "nav.engineSelectorAria": "Motor: {engine}. Abrir seletor",
  "nav.prefsAria": "Preferências de moeda e idioma",
  "nav.currency": "Moeda",
  "nav.language": "Idioma",
  "nav.signIn": "Entrar",
  "nav.signInShort": "Entrar",
  "nav.signUp": "Criar conta",
  "nav.signUpShort": "Criar conta",
  "nav.home": "Início",
  "nav.explore": "Explorar",
  "nav.howItWorks": "Como funciona?",
  "nav.userMenuAria": "Menu do usuário",
  "nav.signOut": "Sair",
  "nav.continueWithGoogle": "Continuar com o Google",
  "nav.hamburgerMenuAria": "Menu",
  "footer.terms": "Termos",
  "footer.privacy": "Privacidade",
  "footer.cookies": "Cookies",
  "footer.affiliates": "Afiliados",
  "footer.contact": "Contato",
  "footer.positioning": "Comparador de experiências de viagem. Você sempre reserva com o fornecedor original.",

  "auth.popover.signInHeading": "Entrar",
  "auth.popover.signUpHeading": "Crie sua conta",
  "auth.popover.subcopy": "Salve suas preferências e acesse de qualquer dispositivo.",
  "auth.email.label": "E-mail",
  "auth.email.placeholder": "voce@email.com",
  "auth.email.submit": "Enviar link de acesso",
  "auth.email.sending": "Enviando…",
  "auth.email.sent": "Verifique seu e-mail — enviamos um link para entrar.",
  "auth.email.orDivider": "ou",
  "auth.close": "Fechar",
  "auth.error.auth_not_configured": "A autenticação não está disponível no momento.",
  "auth.error.auth_email_required": "Digite um e-mail.",
  "auth.error.auth_email_failed": "Não conseguimos enviar o link. Tente novamente.",
  "auth.popover.wishlistSubcopy": "Entre para salvar experiências na sua lista de desejos.",
  "auth.popover.comparisonSubcopy": "Entre para salvar esta comparação.",

  "account.title": "Minha conta",
  "account.loggedOutHeading": "Entre para continuar",
  "account.loggedOutBody": "Salve seu idioma e moeda preferidos e acesse de qualquer dispositivo.",
  "account.email": "E-mail",
  "account.memberSince": "Membro desde {date}",
  "account.preferencesHeading": "Preferências",
  "account.notConfigured": "A autenticação não está configurada nesta instância.",
  "account.backHome": "Voltar ao início",

  "account.menu.profile": "Perfil",
  "account.menu.recentSearches": "Buscas recentes",
  "account.menu.wishlist": "Lista de desejos",
  "account.menu.savedComparisons": "Comparações salvas",
  "account.menu.preferences": "Preferências",
  "account.menu.help": "Ajuda e contato",

  "account.tabs.overview": "Resumo",
  "account.tabs.profile": "Perfil",
  "account.tabs.sitePreferences": "Preferências do site",
  "account.tabs.travelPreferences": "Preferências de viagem",
  "account.tabs.recentSearches": "Buscas recentes",
  "account.tabs.wishlist": "Lista de desejos",
  "account.tabs.savedComparisons": "Comparações salvas",
  "account.tabs.help": "Ajuda e contato",

  "account.overview.heading": "Resumo",
  "account.overview.recentSearchesCount": "{count} buscas recentes",
  "account.overview.wishlistCount": "{count} experiências salvas",
  "account.overview.savedComparisonsCount": "{count} comparações salvas",
  "account.overview.completionHeading": "Status do perfil",
  "account.overview.completionCount": "{completed} de {total} concluído",
  "account.overview.quickActionsHeading": "Ações rápidas",
  "account.overview.quickAction.search": "Buscar experiências",
  "account.overview.quickAction.wishlist": "Ver lista de desejos",
  "account.overview.quickAction.comparisons": "Ver comparações",

  "account.sitePreferences.heading": "Preferências do site",

  "account.travelPreferences.heading": "Preferências de viagem",
  "account.travelPreferences.body": "Isto é o que aprendemos com suas buscas até agora — ainda não editável.",
  "account.travelPreferences.empty": "Ainda não aprendemos suas preferências de viagem. Gere um roteiro para começar.",
  "account.travelPreferences.generationCount": "{count} roteiros gerados",
  "account.travelPreferences.citiesExplored": "{count} cidades exploradas",
  "account.travelPreferences.topInterests": "Interesses frequentes",
  "account.travelPreferences.interestsHeading": "Escolha seus interesses de viagem",
  "account.travelPreferences.interestsBody": "Salve seus interesses no seu perfil.",
  "account.travelPreferences.save": "Salvar interesses",
  "account.travelPreferences.saving": "Salvando…",
  "account.travelPreferences.saved": "Interesses salvos",
  "account.travelPreferences.saveError": "Não conseguimos salvar seus interesses. Tente novamente.",
  "account.travelPreferences.learnedHeading": "Aprendido automaticamente",

  "account.recentSearches.heading": "Buscas recentes",
  "account.recentSearches.empty": "Busque experiências para salvar suas buscas recentes",
  "account.recentSearches.emptyTitle": "Você ainda não tem buscas recentes",
  "account.recentSearches.emptyCta": "Buscar experiências",
  "account.recentSearches.rerun": "Buscar novamente",
  "account.recentSearches.remove": "Remover",
  "account.recentSearches.removeAria": "Remover busca recente: {query}",

  "account.wishlist.heading": "Lista de desejos",
  "account.wishlist.empty": "Salve experiências com o coração",
  "account.wishlist.emptyTitle": "Sua lista de desejos está vazia",
  "account.wishlist.emptyCta": "Explorar experiências",
  "account.wishlist.viewProvider": "Ver oferta",
  "account.wishlist.remove": "Remover",
  "account.wishlist.removeAria": "Remover da lista de desejos: {itemTitle}",

  "account.savedComparisons.heading": "Comparações salvas",
  "account.savedComparisons.empty": "Compare atividades e salve sua comparação",
  "account.savedComparisons.emptyTitle": "Você ainda não tem comparações salvas",
  "account.savedComparisons.emptyCta": "Buscar experiências",
  "account.savedComparisons.itemCount": "{count} experiências",
  "account.savedComparisons.remove": "Excluir",
  "account.savedComparisons.removeAria": "Excluir comparação salva",

  "account.help.heading": "Ajuda e contato",
  "account.help.body": "Dúvidas ou comentários sobre o Sift Trip? Fale conosco.",
  "account.help.cta": "Ir para contato",

  "account.profile.formHeading": "Complete seu perfil",
  "account.profile.displayNameLabel": "Nome",
  "account.profile.displayNamePlaceholder": "Seu nome",
  "account.profile.phoneLabel": "Telefone (opcional)",
  "account.profile.phonePlaceholder": "+55 11 00000-0000",
  "account.profile.homeCountryLabel": "País de residência (opcional)",
  "account.profile.homeCountryPlaceholder": "Brasil",
  "account.profile.travelerTypeLabel": "Como você costuma viajar? (opcional)",
  "account.profile.save": "Salvar alterações",
  "account.profile.saving": "Salvando…",
  "account.profile.saved": "Perfil atualizado",
  "account.profile.saveError": "Não conseguimos salvar suas alterações. Tente novamente.",

  "account.travelerType.solo": "Sozinho/a",
  "account.travelerType.pareja": "Em casal",
  "account.travelerType.familia": "Em família",
  "account.travelerType.amigos": "Com amigos",
  "account.travelerType.mayores": "Adultos mais velhos",

  "account.interest.art_culture": "Arte e cultura",
  "account.interest.history_heritage": "História",
  "account.interest.nature_outdoors": "Natureza",
  "account.interest.beach_water": "Praia",
  "account.interest.food_drink": "Comida",
  "account.interest.local_life": "Vida local",
  "account.interest.museums_tickets": "Museus",
  "account.interest.adventure": "Aventura",
  "account.interest.family_kids": "Família",
  "account.interest.romance": "Romântico",
  "account.interest.nightlife": "Vida noturna",
  "account.interest.free_tours": "Free tours",
  "account.interest.transfers_airport": "Traslados",

  "wishlist.save": "Salvar na lista de desejos",
  "wishlist.remove": "Remover da lista de desejos",
  "wishlist.error": "Não conseguimos salvar. Tente novamente.",

  "compare.saveComparison": "Salvar comparação",
  "compare.saveComparison.saved": "Comparação salva",
  "compare.saveComparison.error": "Não conseguimos salvar a comparação.",

  "contact.title": "Contato",
  "contact.intro": "Para perguntas sobre o SiftTrip, parcerias, afiliados, fornecedores, privacidade ou suporte geral, você pode nos escrever pelos canais oficiais abaixo.",
  "contact.channels.heading": "Canais oficiais",
  "contact.email.label": "E-mail",
  "contact.phone.label": "WhatsApp / Telefone",
  "contact.whatsapp.cta": "Escrever no WhatsApp",
  "contact.call.cta": "Ligar",
  "contact.social.heading": "Redes sociais",
  "contact.social.body": "Você também pode seguir os canais oficiais do SiftTrip para novidades, destinos e informações de lançamento.",
  "contact.supportExpectation.heading": "Antes de escrever",
  "contact.supportExpectation.body": "Se sua dúvida é sobre uma reserva, entre em contato primeiro com o fornecedor onde você concluiu a compra.",
  "contact.provider.heading": "Fornecedores e parceiros",
  "contact.provider.body": "Você é fornecedor ou parceiro? Fale conosco.",
  "contact.backHome": "Voltar ao SiftTrip",

  "legal.onlyInSpanishNote": "Disponível em espanhol por enquanto.",
};

export const MESSAGES: Record<Language, Record<MessageKey, string>> = { es, en, pt };
